<script lang="ts">
export default {
  name: 'ClientViewOverlay',
};
</script>

<script setup lang="ts">
import { X, ChevronsDown } from '@lucide/vue';
import { CollapsibleContent, CollapsibleRoot, CollapsibleTrigger } from 'reka-ui';
import { AppState, Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { UserPermission } from '@/types/UserPermissions';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';

const store = useStore<AppState>();
const router = useRouter();
const route = useRoute();
const returnRoute = ref('');
const returnQuery = ref({});
const isOpen = ref(false);
const collapsibleRef = ref<HTMLDivElement | null>(null);
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null);

// List of permissions for the collapsible dropdown
const permissions = ref([
  { id: UserPermission.OverviewRead, label: 'Overview', checked: false },
  { id: UserPermission.PoliciesRead, label: 'Policies', checked: false },
  { id: UserPermission.TowersRead, label: 'Towers', checked: false },
  { id: UserPermission.ProgramsRead, label: 'Programs', checked: false },
  { id: UserPermission.DocumentsRead, label: 'Documents', checked: false },
  { id: UserPermission.RenewalRead, label: 'Renewals', checked: false },
  { id: UserPermission.AnalyticsRead, label: 'Analytics', checked: false },
  { id: UserPermission.DocumentsWrite, label: 'Document Upload', checked: false },
  { id: UserPermission.ClaimsRead, label: 'Claims', checked: false },
  { id: UserPermission.CardviewRead, label: 'Card View', checked: false },
]);

// set the permissions that are in the user's permissions array
for (const userPermission of store.state.clientViewState.permissions) {
  for (const permission of permissions.value) {
    if (permission.id === userPermission) {
      permission.checked = true;
    }
  }
}

// when entering client view, redirect the user to the appropriate route
const routeMap = new Map<string, string>([
  ['ClientView', 'Home'],
  ['AccountList', 'Home'],
  ['account-benchmarking', 'account-overview'],
  ['account-benchmarking-selected', 'account-overview'],
  ['account-tower-detail', 'account-overview'],
  ['account-towers', 'account-overview'],
  ['account-exposure', 'account-overview'],
  ['account-claim-detail', 'account-overview'],
  ['account-cyber', 'account-overview'],
  ['account-quotes', 'account-overview'],
  ['account-quote-program', 'account-overview'],
  ['account-renewal', 'account-overview'],
  ['account-client-readiness', 'account-overview'],
  ['account-analytics', 'account-overview'],
]);

async function handleClientViewClose() {
  // Show confirmation dialog before exiting client view
  const confirmed = await confirmModalRef.value?.showConfirm('Are you sure you want to exit Client View?');
  
  if (!confirmed) {
    return;
  }

  Commit(StoreMutationType.DISABLE_CLIENT_VIEW_MODE);

  const routeName = route.name?.toString();
  if (routeName && routeMap.has(routeName)) {
    router.push({ name: routeMap.get(routeName) });
  } else if (returnRoute.value && routeName === returnRoute.value) {
    // Redirect to the original route with params if still on this route
    router.push({ name: routeName, query: returnQuery.value });
  }
}

function handleClickOutside(event: MouseEvent) {
  if (collapsibleRef.value && !collapsibleRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
}

function handleTogglePermission(permission: UserPermission, checked: boolean) {
  if (checked) {
    Commit(StoreMutationType.ADD_CLIENT_VIEW_PERMISSION, permission);
  } else {
    Commit(StoreMutationType.REMOVE_CLIENT_VIEW_PERMISSION, permission);
  }
}

onMounted(() => {
  returnRoute.value = route.name?.toString() || '';
  returnQuery.value = { ...route.query };

  const routeName = route.name?.toString();
  if (routeName && routeMap.has(routeName)) {
    router.push({ name: routeMap.get(routeName) });
  } else {
    router.push({ name: routeName, query: {} });
  }

  document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<template>
  <div
    v-if="store.state.clientViewState.isClientViewEnabled"
    class="pointer-events-none absolute left-0 top-0 z-2000 h-full w-full">
    <div
      class="sticky top-0 flex h-screen w-full justify-center border-4 border-solid border-green-500">
      <div ref="collapsibleRef" class="pointer-events-auto absolute top-0">
        <CollapsibleRoot
          v-model:open="isOpen"
          class="border border-solid border-green-500 bg-green-500 text-xs text-black">
          <!-- Header with trigger and close button -->
          <div class="flex items-center justify-between gap-2 px-2 pb-1">
            <CollapsibleTrigger class="flex w-full items-center justify-center gap-2">
              <ChevronsDown
                :size="14"
                class="cursor-pointer stroke-black stroke-2 transition-transform"
                :class="{ 'rotate-180': isOpen }" />
              <span class="font-medium">Client View</span>
            </CollapsibleTrigger>

            <div class="tooltip tooltip-bottom tooltip-error" data-tip="Close Client View">
              <X
                :size="16"
                class="cursor-pointer stroke-red-900 stroke-[3] transition-transform hover:scale-110"
                @click="handleClientViewClose" />
            </div>
          </div>

          <!-- Collapsible content with permissions -->
          <CollapsibleContent class="CollapsibleContent border-t border-green-600 bg-green-200">
            <div class="space-y-2 px-3 py-2">
              <div class="text-xs font-semibold text-black/80">Permissions:</div>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1">
                <label
                  v-for="permission in permissions"
                  :key="permission.id"
                  class="flex cursor-pointer items-center gap-2 rounded-sm px-1 py-0.5 hover:bg-green-600/20">
                  <input
                    type="checkbox"
                    v-model="permission.checked"
                    @click="handleTogglePermission(permission.id, !permission.checked)"
                    class="checkbox checkbox-xs border-solid border-green-700 checked:bg-green-700" />
                  <span class="select-none whitespace-nowrap text-xs">{{ permission.label }}</span>
                </label>
              </div>
            </div>
          </CollapsibleContent>
        </CollapsibleRoot>
      </div>
    </div>
    
    <!-- Confirmation Modal -->
    <ConfirmModal ref="confirmModalRef" />
  </div>
</template>

<style>
.CollapsibleContent {
  overflow: hidden;
}
.CollapsibleContent[data-state='open'] {
  animation: slideDown 150ms ease-out;
}
.CollapsibleContent[data-state='closed'] {
  animation: slideUp 150ms ease-out;
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--reka-collapsible-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-collapsible-content-height);
  }
  to {
    height: 0;
  }
}
</style>
